import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  TRANSLATION_DOC_LANDINGS,
  getTranslationDocLanding,
  type TranslationDocLanding,
} from "@/content/translation-corpus/documents";

function faqsTh(d: TranslationDocLanding) {
  return [
    { q: `${d.titleTh} ใช้ยื่นกับหน่วยงานไหนได้บ้าง?`, a: d.useCaseTh },
    { q: `ต้องเตรียมอะไรมาบ้าง?`, a: d.prepareTh.join(" · ") },
    { q: `ต้องใช้ระบบรับรองคำแปลแบบไหน?`, a: d.certSystemTh },
    { q: `สาเหตุที่คำแปลถูกตีกลับบ่อยคืออะไร?`, a: d.pitfallsTh.join(" · ") },
    { q: `หลังแปลเสร็จต้องทำอะไรต่อ?`, a: d.nextStepTh },
    { q: `ใช้เวลาเท่าไหร่?`, a: `${d.turnaroundTh} · ราคาคิดตามจำนวนหน้าและระบบรับรอง ขอใบเสนอราคาฟรีก่อนได้` },
    {
      q: "การแปล การรับรองคำแปล การรับรองลายมือชื่อ และนิติกรณ์ ต่างกันอย่างไร?",
      a: "เป็นสี่ชั้นแยกกัน: (1) การแปลเนื้อหา (2) คำรับรองความถูกต้องของผู้แปล (3) การรับรองลายมือชื่อผู้ลงนามโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร และ (4) นิติกรณ์/Apostille ที่รับรองตราประทับเพื่อใช้ข้ามประเทศ สามชั้นแรกไม่สามารถใช้แทนชั้นที่สี่ได้",
    },
    {
      q: "ประเทศไทยใช้ Apostille ได้หรือยัง?",
      a: "ยังไม่มีผลบังคับใช้ครับ ไทยยื่นภาคยานุวัติสารต่ออนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสารเมื่อ 30 มิถุนายน 2569 (2026) และอนุสัญญาจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 (2027) ระหว่างนี้ยังใช้เส้นทางนิติกรณ์กรมการกงสุล + สถานทูตปลายทาง — ตรวจสถานะล่าสุดที่ hcch.net และ consular.mfa.go.th",
    },
    {
      q: "รับประกันได้ไหมว่าปลายทางจะรับคำแปล?",
      a: "การยอมรับเอกสารเป็นดุลพินิจของหน่วยงานผู้รับ เราจึงไม่รับประกันผล แต่เราจะยืนยันระบบรับรองที่ปลายทางกำหนดก่อนเริ่มงาน และแก้ไขงานแปลให้ใหม่โดยไม่คิดค่าใช้จ่ายหากข้อผิดพลาดเกิดจากฝั่งเรา",
    },
  ];
}

export const Route = createFileRoute("/translation/document/$doc")({
  loader: ({ params }: { params: { doc: string } }) => {
    const d = getTranslationDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: TranslationDocPageTH,
  head: ({ params, loaderData }: { params: { doc: string }; loaderData?: { d: TranslationDocLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเภทเอกสาร" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleTh} 2026 — แปลรับรองพร้อมนิติกรณ์`;
    const description = `${d.titleTh}: เอกสารที่ต้องเตรียม ระบบรับรองคำแปลตามประเทศปลายทาง ขั้นตอนนิติกรณ์ และ ${d.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/translation/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/translation/document/${params.doc}` }],
        keywords: [d.titleTh, `${d.titleTh} ราคา`, "แปลเอกสารรับรอง", "รับรองคำแปลกรมการกงสุล", "certified translation"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "แปลเอกสารรับรอง", url: `${site}/translation` },
              { name: d.titleTh, url: `${site}/translation/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/translation/document/${params.doc}`)) },
      ],
    };
  },
});

function TranslationDocPageTH() {
  const { d } = Route.useLoaderData();
  const faqs = faqsTh(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/translation" className="hover:underline">แปลเอกสารรับรอง</a> <span>/</span>{" "}
          <span>{d.titleTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleTh} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          เอกสารอ้างอิง: <strong>{d.officialTh}</strong> — ระบบรับรองคำแปล: {d.certSystemTh}
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/translation/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อยงานแปล</a>
          <a href={`/en/translation/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้ทำอะไร / ยื่นกับใคร</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">เตรียมเอกสารก่อนส่งแปล</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {d.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">สาเหตุที่คำแปลถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">ขั้นตอนถัดไปหลังแปลเสร็จ</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.nextStepTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {d.titleTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">แปลรับรองเอกสารประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/translation/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          ข้อมูล ณ สิงหาคม 2569 (2026) — ขั้นตอนนิติกรณ์อ้างอิงกองสัญชาติและนิติกรณ์ กรมการกงสุล (consular.mfa.go.th)
          · สถานะอนุสัญญา Apostille อ้างอิง hcch.net · ระบบผู้แปลรับรองออสเตรเลียอ้างอิง naati.com.au
          — โปรดตรวจสอบข้อกำหนดกับหน่วยงานผู้รับก่อนดำเนินการ
        </p>
        <div className="mb-10">
          <PageGallery category="translation" seed="translation-document.doc" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}